import { Building2, Store, Truck, Warehouse } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * De onde sai → para onde vai, para bater o olho: a filial que emite (com o
 * centro que baixa o estoque) à esquerda, o destino à direita e o caminhão
 * andando no meio. Em tela estreita empilha, com a seta para baixo.
 */
export interface PontaRota {
  empresa: number;
  fantasia: string | null;
  uf: string | null;
  centro: number | null;
  centroNome: string | null;
  /** Só no destino: armazém ou Full (muda o ícone). */
  tipo?: 'ARMAZEM' | 'FULL';
  nome?: string;
}

export function Rota({ origem, destino, legenda, compacta = false }: { origem: PontaRota; destino: PontaRota; legenda?: string; compacta?: boolean }): JSX.Element {
  return (
    <div className="space-y-1.5">
    <div className={cn('flex flex-col items-stretch gap-2 sm:flex-row sm:items-center', compacta ? 'text-xs' : 'text-[13px]')}>
      <style>{`@keyframes wms-rota { 0% { left: 0%; } 100% { left: calc(100% - 1.25rem); } }
        @keyframes wms-rota-v { 0% { top: 0%; } 100% { top: calc(100% - 1.25rem); } }`}</style>
      <Ponta p={origem} papel="sai" />
      <div className="relative flex min-h-[44px] flex-1 flex-col items-center justify-center sm:min-h-[24px] sm:min-w-[56px]">
        {/* trilho */}
        <span className="absolute inset-y-0 left-1/2 w-0 border-l-2 border-dashed border-primaria/40 sm:inset-x-0 sm:inset-y-auto sm:left-0 sm:top-1/2 sm:h-0 sm:w-full sm:border-l-0 sm:border-t-2" aria-hidden />
        <Truck
          className="absolute h-5 w-5 rounded bg-superficie text-primaria motion-reduce:hidden max-sm:left-1/2 max-sm:-translate-x-1/2 max-sm:[animation:wms-rota-v_2.6s_ease-in-out_infinite_alternate] sm:top-1/2 sm:-translate-y-1/2 sm:[animation:wms-rota_2.6s_ease-in-out_infinite_alternate]"
          aria-hidden
        />
      </div>
      <Ponta p={destino} papel="chega" />
    </div>
    {legenda ? (
      <p className="text-center">
        <span className="whitespace-nowrap rounded-full border border-borda bg-superficie px-2 py-0.5 text-2xs font-semibold text-textoFraco">{legenda}</span>
      </p>
    ) : null}
    </div>
  );
}

function Ponta({ p, papel }: { p: PontaRota; papel: 'sai' | 'chega' }): JSX.Element {
  const Icone = papel === 'sai' ? Building2 : p.tipo === 'FULL' ? Store : Warehouse;
  return (
    <div
      className={cn(
        'flex min-w-0 items-center gap-2.5 rounded-xl border px-3 py-2 sm:w-[42%]',
        papel === 'sai' ? 'border-borda bg-superficie2' : 'border-primaria/40 bg-primaria-suave/40',
      )}
    >
      <span
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
          papel === 'sai' ? 'bg-superficie text-textoFraco' : 'bg-primaria text-white',
        )}
      >
        <Icone className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <p className="text-2xs font-semibold uppercase tracking-wide text-textoSuave">{papel === 'sai' ? 'Sai de' : 'Vai para'}</p>
        <p className="truncate font-bold" title={p.nome ?? p.fantasia ?? undefined}>
          {p.nome ?? p.fantasia ?? `Empresa ${p.empresa}`} {p.uf ? <span className="font-semibold text-textoFraco">· {p.uf}</span> : null}
        </p>
        <p className="truncate text-2xs text-textoFraco" title={p.centroNome ?? undefined}>
          empresa {p.empresa}
          {p.nome && p.fantasia ? ` (${p.fantasia})` : ''}
          {p.centro ? ` · centro ${p.centro}` : ''}
          {p.centroNome ? ` ${p.centroNome.toLowerCase()}` : ''}
        </p>
      </div>
    </div>
  );
}
